import React, { useContext, useEffect, useState } from "react";
import { Card, CardBody, Col, Row } from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import {
   AccountInformation, AttachmentManager, CurrentInventory, CurrentOrders, CurrentSampleItems,
   // CustomerPriceQuotes DISABLED 2026-07-30 - see the "Price Quotes" card below
   CustomerCorrectiveActions, CustomerInformation, /*CustomerPriceQuotes,*/ CustomerQuotes,
   CustomerTypeahead, FilterButton, InvoiceSummary, ServiceThreadSpinner,
} from ".";
import { DesktopContainer } from "../layouts";
import { api, constants, filterHelpers, storage, UserContext } from "../utils";
import { toast } from "react-toastify";
import useCookies from "react-use-cookie";

export default function CustomerDashboard(props) {
   const userCtx = useContext(UserContext);
   const location = useLocation();
   const navigate = useNavigate();
   const { customerId } = useParams();
   const [customer, setCustomer] = useState(location.state);
   // Active-customer persistence now lives in App.js via useActiveCustomer (multi-account plan 5.1) so
   // it is app-wide rather than owned by this screen. The dashboard filter cookie below stays local -
   // it is this screen's tile layout, not the active account.
   const activeCustomer = userCtx.activeCustomer;
   const [filterCookies, setFilterCookie] = useCookies(constants.LocalStorageKeys.CustomerDashboard, null);
   const [filters, setFilters] = useState([]);
   const [loadingComplete, setLoadingComplete] = useState(false);

   useEffect(() => {
      let foundCookies = filterCookies;
      if (foundCookies && foundCookies !== "null") {
         let parsedCookies = JSON.parse(foundCookies);
         _.each(parsedCookies, (opt) => {
            if (opt.filteredDisplay === "Account Info") {
               opt.filteredDisplay = "Account";
            }
            if (opt.filteredDisplay === "Current Orders") {
               opt.filteredDisplay = "Orders";
            }
            if (opt.filteredDisplay === "Current Inventory") {
               opt.filteredDisplay = "Inventory";
            }
            if (opt.filteredDisplay === "Sample Inventory") {
               opt.filteredDisplay = "Samples";
            }
            if (opt.filteredDisplay === "Corrective Action History") {
               opt.filteredDisplay = "CARS";
            }
            if (opt.filteredDisplay === "Invoice Summary") {
               opt.filteredDisplay = "Invoices";
            }
            if (opt.filteredDisplay === "Customer Info") {
               opt.filteredDisplay = "Customer";
            }
         });
         let filterList = _.map(parsedCookies, (c) => c);
         setFilters(filterList);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      activeCustomer.persist(customer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [customer]);

   useEffect(() => {
      const customerCookieFallback = activeCustomer.readFromStorageFallback();
      // Customer-role users resolve to one of their OWN assigned accounts and nothing else. A route
      // param, a stale cookie, or (critically) an admin's last-used account bleeding in during
      // impersonation are all treated as untrusted CANDIDATES: honored only if the candidate is a
      // member of this user's set, otherwise ignored in favor of their primary. Before multi-account
      // this branch force-loaded the single CustomerId; the outcome is identical while a customer has
      // one account, and becomes meaningful once they have several.
      if (userCtx.currentUser?.isAnyCustomerTypeRole) {
         const candidate = customerId
            ? { label: customerId, value: customerId }
            : (activeCustomer.hasStoredCustomer
               ? activeCustomer.readFromCookie()
               : customerCookieFallback);
         const resolved = resolveCustomerAccount(candidate);
         if (resolved) {
            setCustomer(resolved);
            userCtx.setCustomerFilter(resolved);
            userCtx.loadCart(resolved.value);
         } else {
            // No usable account is the misconfig/leak sentinel - fail closed, load nobody.
            setCustomer(null);
            userCtx.setCustomerFilter(null);
         }
         setLoadingComplete(true);
         return;
      }
      if (customerId) {
         let jsonCustomerId = JSON.stringify({
            value: customerId,
            label: customerId
         });
         const json = decodeURI(jsonCustomerId);
         const opt = JSON.parse(json);
         api
            .fetch(`Customer/FastLookUp/${opt.value}`) //figure out what opt.value is and try to pass the same thing
            .then((response) => {
               if (response.data?.success) {
                  setCustomer(response.data.customer);
                  userCtx.setCustomerFilter(response.data.customer);
               } else {
                  throw new Error(response.data?.message || "An error has occurred");
               }
            })
            .catch((error) => {
               toast.error("An error has occured, please contact your admin");
               api.catchHandler(error);
            });
      } else if (!location.state && (activeCustomer.hasStoredCustomer || customerCookieFallback)) {
         const newCustomer = activeCustomer.hasStoredCustomer
            ? activeCustomer.readFromCookie()
            : customerCookieFallback;
         const isCustomerUser = userCtx.currentUser && userCtx.currentUser.isAnyCustomerTypeRole;
         if (!isCustomerUser && newCustomer && newCustomer.value) {
            // Internal users: the customer cached here (up to 60 days) may have been deactivated
            // since it was saved. Re-validate against the active-only lookup and, if it is no longer
            // active, clear it and notify rather than silently loading broken/empty dashboard tiles.
            // (Customer-role users are handled at login and keep their forced-customer path below.)
            api
               .fetch(`Customer/FastLookUp/${newCustomer.value}`)
               .then((response) => {
                  const found = response.data?.success ? response.data.customer : null;
                  if (found) {
                     setCustomer(found);
                     userCtx.setCustomerFilter(found);
                     userCtx.loadCart(found.value);
                  } else {
                     activeCustomer.clear();
                     setCustomer(null);
                     userCtx.setCustomerFilter(null);
                     userCtx.loadActiveCarts();
                     toast.info(`Customer ${newCustomer.label || newCustomer.value} is no longer active. Please select a different customer.`);
                  }
               })
               .catch(api.catchHandler)
               .finally(() => setLoadingComplete(true));
            return;
         }
         setCustomer(newCustomer);
         if (userCtx && userCtx.setCustomerFilter) {
            userCtx.setCustomerFilter(newCustomer);
            if (newCustomer && newCustomer.value) {
               userCtx.loadCart(newCustomer.value);
            } else {
               userCtx.loadActiveCarts();
            }
         }
      } else if (location.state) {
         setCustomer(location.state);
      } else if (userCtx.currentUser && userCtx.currentUser.isAnyCustomerTypeRole) {
         // if all else fails cookies might be disabled
         console.error('Could not set customer context!');
      }
      setLoadingComplete(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [location]);

   // Decide which account a customer-role user lands on. NEVER trust a passed or stored value: honor
   // the candidate only when it is one of the accounts actually assigned to this user
   // (currentUser.customerIds, projected server-side), otherwise fall back to their primary
   // (currentUser.customerId, which login has already healed to an ACTIVE account - plan step 6).
   // Returns null when there is no usable account so the caller can fail closed.
   //
   // The membership test is the client-side twin of AppHelpers.CleanCriteria on the server - the
   // server remains the authority and re-validates every request; this only keeps the UI from
   // presenting an account the server would refuse.
   function resolveCustomerAccount(candidate) {
      const primary = userCtx.currentUser?.customerId;
      const assigned = userCtx.currentUser?.customerIds ?? [];
      const sameAccount = (a, b) =>
         !!a && !!b && String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
      const isAssigned = (id) => assigned.some((a) => sameAccount(a, id));

      if (candidate?.value && isAssigned(candidate.value)) return candidate;
      return primary ? { label: primary, value: primary } : null;
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      saveSearchCookie(JSON.stringify(Object.assign({}, filterChanges)));
      setFilters(filterChanges);
   }

   function saveSearchCookie(data) {
      try {
         storage.setItem(constants.LocalStorageKeys.CustomerDashboard, data);
         setFilterCookie(data, {
            // path: location.pathname,
            domain: window.location.hostname,
            sameSite: "Strict",
            days: 60,
         });
      } catch {
         console.error("Failed to save search cookie");
      }
   }

   return (
      (document.title = `Customer Dashboard`),
      (
         <DesktopContainer screenName="Customer Dashboard">
            <ServiceThreadSpinner on={!loadingComplete}>
               <div className="filter-header" style={{ zIndex: "100" }}>
                  {userCtx.currentUser && !userCtx.currentUser.isAnyCustomerTypeRole ? (
                     <Row className="justify-content-center mb-2">
                        <Col xs="3" lg="2" className="text-end">
                           <h4 className="pt-1 text-white">Customer:</h4>
                        </Col>
                        <Col xs="9" lg="5" className="pe-4">
                           <CustomerTypeahead
                              value={customer}
                              onChange={(opt) =>
                                 navigate(constants.ENDPOINTS.customerDashboard, {
                                    state: opt,
                                    replace: true,
                                 })
                              }
                           />
                        </Col>
                        <Col xs="1" />
                     </Row>
                  ) : null}
                  <div className="dashboard-button-container">
                     <FilterButton
                        filterName="accountInformation"
                        displayName="Account"
                        value={filterHelpers.getValue(filters, "accountInformation")}
                        onChangeCallback={onFilterChange}
                     />
                     <FilterButton
                        filterName="currentOrders"
                        displayName="Orders"
                        displayCaptionClassName="fontSize85 filter-definition-caption"
                        value={filterHelpers.getValue(filters, "currentOrders")}
                        onChangeCallback={onFilterChange}
                     />
                     <FilterButton
                        filterName="currentQuotes"
                        displayName="Quotes"
                        displayCaptionClassName="fontSize85 filter-definition-caption"
                        value={filterHelpers.getValue(filters, "currentQuotes")}
                        onChangeCallback={onFilterChange}
                     />
                     <FilterButton
                        filterName="currentInventory"
                        displayName="Inventory"
                        displayCaptionClassName="fontSize85 filter-definition-caption"
                        value={filterHelpers.getValue(filters, "currentInventory")}
                        onChangeCallback={onFilterChange}
                     />
                     <FilterButton
                        filterName="sampleItems"
                        displayName="Samples"
                        displayCaptionClassName="fontSize85 filter-definition-caption"
                        value={filterHelpers.getValue(filters, "sampleItems")}
                        onChangeCallback={onFilterChange}
                     />
                     <FilterButton
                        filterName="invoiceSummary"
                        displayName="Invoices"
                        displayCaptionClassName="fontSize85 filter-definition-caption"
                        value={filterHelpers.getValue(filters, "invoiceSummary")}
                        onChangeCallback={onFilterChange}
                     />
                     {userCtx.currentUser &&
                        userCtx.currentUser &&
                        !userCtx.currentUser.isAnyCustomerTypeRole ? (
                        <FilterButton
                           filterName="correctiveActions"
                           displayName="CARS"
                           displayCaptionClassName="fontSize85 filter-definition-caption"
                           value={filterHelpers.getValue(filters, "correctiveActions")}
                           onChangeCallback={onFilterChange}
                        />
                     ) : null}
                     {!_.isNil(userCtx) &&
                        userCtx.currentUser &&
                        !userCtx.currentUser.isAnyCustomerTypeRole && (
                           <FilterButton
                              filterName="attachments"
                              displayName="Attachments"
                              displayCaptionClassName="fontSize85 filter-definition-caption"
                              value={filterHelpers.getValue(filters, "attachments")}
                              onChangeCallback={onFilterChange}
                           />
                        )
                     }
                     {/* "Price Quotes" DISABLED 2026-07-30 per Jay: cost quoting moved to the Focus
                         site. Commented out rather than deleted until we are certain it is not wanted
                         here again. A leftover costQuotes:true in a user's saved dashboard cookie is
                         harmless with both this button and the card below gone. */}
                     {/* {!_.isNil(userCtx) &&
                        userCtx.currentUser &&
                        !userCtx.currentUser.isAnyCustomerTypeRole && (
                           <FilterButton
                              filterName="costQuotes"
                              displayName="Price Quotes"
                              displayCaptionClassName="fontSize85 filter-definition-caption"
                              value={filterHelpers.getValue(filters, "costQuotes")}
                              onChangeCallback={onFilterChange}
                           />
                        )
                     } */}
                     <FilterButton
                        filterName="customerInformation"
                        displayName="Customer"
                        displayCaptionClassName="fontSize85 filter-definition-caption"
                        value={filterHelpers.getValue(filters, "customerInformation")}
                        onChangeCallback={onFilterChange}
                     />
                  </div>
               </div>
               {customer && (
                  <>
                     {filterHelpers.getValue(filters, "accountInformation") && (
                        <Card className="dashboard-card">
                           <CardBody>
                              <AccountInformation customerId={customer.value} />
                           </CardBody>
                        </Card>
                     )}

                     {filterHelpers.getValue(filters, "currentOrders") && (
                        <Card className="dashboard-card">
                           <CardBody>
                              <CurrentOrders customerId={customer.value} />
                           </CardBody>
                        </Card>
                     )}

                     {filterHelpers.getValue(filters, "currentQuotes") && (
                        <Card className="dashboard-card">
                           <CardBody>
                              <CustomerQuotes customerId={customer.value} />
                           </CardBody>
                        </Card>
                     )}

                     {filterHelpers.getValue(filters, "currentInventory") && (
                        <Card className="dashboard-card">
                           <CardBody>
                              <CurrentInventory
                                 customerId={customer.value}
                                 customerName={customer.label}
                              />
                           </CardBody>
                        </Card>
                     )}

                     {filterHelpers.getValue(filters, "sampleItems") && (
                        <Card className="dashboard-card">
                           <CardBody>
                              <CurrentSampleItems
                                 customerId={customer.value}
                                 customerName={customer.label}
                              />
                           </CardBody>
                        </Card>
                     )}

                     {filterHelpers.getValue(filters, "invoiceSummary") && (
                        <Card className="dashboard-card">
                           <CardBody>
                              <InvoiceSummary
                                 customerId={customer.value}
                                 customerName={customer.label}
                              />
                           </CardBody>
                        </Card>
                     )}

                     {userCtx.currentUser &&
                        userCtx.currentUser &&
                        !userCtx.currentUser.isAnyCustomerTypeRole
                        ? filterHelpers.getValue(filters, "correctiveActions") && (
                           <Card className="dashboard-card">
                              <CardBody>
                                 <CustomerCorrectiveActions
                                    customerId={customer.value}
                                 />
                              </CardBody>
                           </Card>
                        )
                        : null}

                     {/* "Price Quotes" card DISABLED 2026-07-30 per Jay - see the filter button above.
                         The endpoints it called (Engineering GetCostQuotesByCust /
                         ExportCustomerCostQuotes / DeleteCostQuote and Report CustPriceQuote) are
                         commented out with it. */}
                     {/* {userCtx.currentUser &&
                        userCtx.currentUser &&
                        !userCtx.currentUser.isAnyCustomerTypeRole
                        ? filterHelpers.getValue(filters, "costQuotes") && (
                           <Card className="dashboard-card">
                              <CardBody>
                                 <CustomerPriceQuotes
                                    customerId={customer.value}
                                 />
                              </CardBody>
                           </Card>
                        )
                        : null} */}

                     {userCtx.currentUser &&
                        userCtx.currentUser &&
                        !userCtx.currentUser.isAnyCustomerTypeRole
                        ? filterHelpers.getValue(filters, "attachments") && (
                           <Card className="dashboard-card">
                              <CardBody>
                                 <AttachmentManager
                                    attachmentTypeId={constants.ATTACHMENT_TYPES.Customer}
                                    contextName={`#${customer.value}`}
                                    attachmentKey={customer.value}
                                 />
                              </CardBody>
                           </Card>
                        )
                        : null}

                     {filterHelpers.getValue(filters, "customerInformation") && (
                        <Card className="dashboard-card">
                           <CardBody>
                              <CustomerInformation customerId={customer.value} />
                           </CardBody>
                        </Card>
                     )}
                  </>
               )}
            </ServiceThreadSpinner>
            <br /><br />
         </DesktopContainer>
      )
   );
}
